import { createReducer, on } from '@ngrx/store';
import { AssistantMessage, AssistantState } from '../../shared/models/assistant.model';
import * as AssistantActions from './assistant.actions';

function generateSessionId(): string {
  return `admin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const initialState: AssistantState = {
  currentConversationId: null,
  conversations: [],
  messages: [],
  loading: false,
  error: null,
  conversationsLoading: false,
  adminSessionId: null,
  pendingWrite: null,
  pendingClarification: null,
  adminHistory: [],
  adminSessionState: null,
};

function createId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const assistantReducer = createReducer(
  initialState,

  // Query Actions
  on(AssistantActions.sendQuery, (state, { query, conversationId }) => {
    const userMessage: AssistantMessage = {
      role: 'user',
      content: query,
      createdAt: new Date().toISOString(),
    };

    const activeConversationId = conversationId || state.currentConversationId;
    const nowIso = new Date().toISOString();

    return {
      ...state,
      loading: true,
      error: null,
      messages: [...state.messages, userMessage],
      currentConversationId: activeConversationId,
      conversations: state.conversations
        .map((conv) => {
          if (conv._id !== activeConversationId) {
            return conv;
          }

          return {
            ...conv,
            lastMessageAt: nowIso,
            messageCount: Math.max(1, Number(conv.messageCount || 0) + 1),
          };
        })
        .sort((a, b) => {
          const aTs = Date.parse(String(a.lastMessageAt || 0));
          const bTs = Date.parse(String(b.lastMessageAt || 0));
          return (Number.isNaN(bTs) ? 0 : bTs) - (Number.isNaN(aTs) ? 0 : aTs);
        }),
    };
  }),

  on(AssistantActions.receiveResponse, (state, { response }) => {
    const assistantMessage: AssistantMessage = {
      role: 'assistant',
      content: response.answer,
      createdAt: new Date().toISOString(),
      llmProvider: response.llmProvider,
      llmModel: response.llmModel,
      llmFallbackUsed: response.llmFallbackUsed,
      llmError: response.llmError,
    };

    return {
      ...state,
      loading: false,
      error: null,
      messages: [...state.messages, assistantMessage],
      currentConversationId: response.conversationId || state.currentConversationId,
      pendingWrite: response.pendingWrite ?? null,
      pendingClarification: response.pendingClarification ?? null,
      adminHistory: response.history?.length ? response.history : state.adminHistory,
      adminSessionState: response.sessionState ?? state.adminSessionState,
      adminSessionId: !state.adminSessionId ? generateSessionId() : state.adminSessionId,
    };
  }),

  on(AssistantActions.confirmPendingWrite, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),

  on(AssistantActions.cancelPendingWrite, (state) => ({
    ...state,
    loading: true,
    error: null,
    pendingWrite: null,
  })),

  on(AssistantActions.queryFailed, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Conversation List Actions
  on(AssistantActions.loadConversations, (state) => ({
    ...state,
    conversationsLoading: true,
  })),

  on(AssistantActions.loadConversationsSuccess, (state, { conversations }) => ({
    ...state,
    conversations: conversations.filter((c: any) => {
      const count =
        typeof c?.messageCount === 'number'
          ? c.messageCount
          : Array.isArray(c?.messages)
            ? c.messages.length
            : 0;
      return count > 0;
    }),
    conversationsLoading: false,
    error: null,
  })),

  on(AssistantActions.loadConversationsFailure, (state, { error }) => ({
    ...state,
    conversationsLoading: false,
    error,
  })),

  // Select Conversation (clears messages until new ones load)
  on(AssistantActions.selectConversation, (state, { conversationId }) => ({
    ...state,
    currentConversationId: conversationId,
    messages: [],
    loading: true,
  })),

  // Load Conversation Messages
  on(AssistantActions.loadConversationMessages, (state) => ({
    ...state,
    loading: true,
  })),

  on(AssistantActions.loadConversationMessagesSuccess, (state, { conversation }) => ({
    ...state,
    currentConversationId: conversation._id,
    messages: conversation.messages || [],
    loading: false,
    error: null,
  })),

  on(AssistantActions.loadConversationMessagesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // New Conversation
  on(AssistantActions.newConversation, (state) => ({
    ...state,
    loading: true,
  })),

  on(AssistantActions.newConversationSuccess, (state, { conversation }) => ({
    ...state,
    currentConversationId: conversation._id,
    loading: false,
    error: null,
  })),

  on(AssistantActions.newConversationFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Update Conversation
  on(AssistantActions.updateConversationSuccess, (state, { conversation }) => ({
    ...state,
    conversations: state.conversations.map((conv) =>
      conv._id === conversation._id
        ? {
            ...conv,
            title: conversation.title,
            matterId: conversation.matterId,
            contactId: conversation.contactId,
            isArchived: conversation.isArchived,
          }
        : conv
    ),
  })),

  on(AssistantActions.updateConversationFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Archive Conversation
  on(AssistantActions.archiveConversationSuccess, (state, { conversationId }) => ({
    ...state,
    conversations: state.conversations.filter((conv) => conv._id !== conversationId),
    currentConversationId: state.currentConversationId === conversationId ? null : state.currentConversationId,
    messages: state.currentConversationId === conversationId ? [] : state.messages,
  })),

  // Delete Conversation
  on(AssistantActions.deleteConversationSuccess, (state, { conversationId }) => ({
    ...state,
    conversations: state.conversations.filter((conv) => conv._id !== conversationId),
    currentConversationId: state.currentConversationId === conversationId ? null : state.currentConversationId,
    messages: state.currentConversationId === conversationId ? [] : state.messages,
  })),

  on(AssistantActions.deleteConversationFailure, (state, { error }) => ({
    ...state,
    error,
  })),

  // Clear Messages
  on(AssistantActions.clearMessages, (state) => ({
    ...state,
    messages: [],
    currentConversationId: null,
    pendingWrite: null,
    pendingClarification: null,
    adminHistory: [],
    adminSessionId: null,
    adminSessionState: null,
  })),

  // Clear Error
  on(AssistantActions.clearError, (state) => ({
    ...state,
    error: null,
  })),

  // Hydrate Messages
  on(AssistantActions.hydrateMessages, (state, { messages }) => ({
    ...state,
    messages,
  }))
);
